/**
 * User Detail Modal Component
 * Modal for viewing detailed user information in the admin dashboard
 */

import React from 'react';
import './UserDetailModal.css';

const UserDetailModal = ({ isOpen, onClose, user, onEdit, onDelete, isProcessing }) => {
  if (!isOpen || !user) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status, isActive) => {
    if (!isActive) {
      return <span className="status-badge inactive">Inactive</span>;
    }
    if (status === 'approved') {
      return <span className="status-badge approved">Approved</span>;
    }
    return <span className="status-badge pending">Pending</span>;
  };

  const getRoleBadge = (isSuperuser, isStaff) => {
    if (isSuperuser) {
      return <span className="role-badge superuser">Superuser</span>;
    }
    if (isStaff) {
      return <span className="role-badge staff">Staff</span>;
    }
    return <span className="role-badge user">User</span>;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>User Details - {user.first_name} {user.last_name}</h2>
          <button className="close-btn" onClick={onClose} disabled={isProcessing}>
            ×
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* User Profile Section */}
          <div className="form-section user-profile-section">
            <h3 className="user-name-only">{user.first_name} {user.last_name}</h3>
          </div>

          {/* Basic Information */}
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>User ID</label>
                <div className="info-value">{user.id}</div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email Address</label>
                <div className="info-value">{user.email}</div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>First Name</label>
                <div className="info-value">{user.first_name || 'N/A'}</div>
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <div className="info-value">{user.last_name || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Permissions & Status */}
          <div className="form-section">
            <h3>Permissions & Status</h3>
            <div className="permissions-grid">
              <div className="permission-item">
                <div className="permission-label">Staff Member</div>
                <div className={`permission-status ${user.is_staff ? 'yes' : 'no'}`}>
                  {user.is_staff ? '✅ Yes' : '❌ No'}
                </div>
              </div>
              <div className="permission-item">
                <div className="permission-label">Superuser</div>
                <div className={`permission-status ${user.is_superuser ? 'yes' : 'no'}`}>
                  {user.is_superuser ? '✅ Yes' : '❌ No'}
                </div>
              </div>
              <div className="permission-item">
                <div className="permission-label">Active User</div>
                <div className={`permission-status ${user.is_active ? 'yes' : 'no'}`}>
                  {user.is_active ? '✅ Yes' : '❌ No'}
                </div>
              </div>
              <div className="permission-item">
                <div className="permission-label">Approved</div>
                <div className={`permission-status ${user.is_approved ? 'yes' : 'no'}`}>
                  {user.is_approved ? '✅ Yes' : '❌ No'}
                </div>
              </div>
              <div className="permission-item">
                <div className="permission-label">Email Verified</div>
                <div className={`permission-status ${user.is_email_verified ? 'yes' : 'no'}`}>
                  {user.is_email_verified ? '✅ Yes' : '❌ No'}
                </div>
              </div>
            </div>
          </div>

          {/* Account Timeline */}
          <div className="form-section">
            <h3>Account Timeline</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Date Joined</label>
                <div className="info-value">{formatDate(user.date_joined)}</div>
              </div>
              <div className="form-group">
                <label>Last Updated</label>
                <div className="info-value">{formatDate(user.updated_at)}</div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Created At</label>
                <div className="info-value">{formatDate(user.created_at)}</div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          {user.phone && (
            <div className="form-section">
              <h3>Contact Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number</label>
                  <div className="info-value">{user.phone}</div>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isProcessing}
          >
            Close
          </button>
          {onEdit && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onEdit(user)}
              disabled={isProcessing}
            >
              Edit User
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => onDelete(user.id)}
              disabled={isProcessing}
            >
              Delete User
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;
